import moment from "moment";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import {
  MY_LEAVE_GET_WORK_DAYS,
  MY_LEAVE_WORK_DAYS_SUBSCRIPTION,
} from "my-leave/graphql";
import { useOcPolling } from "oc/hooks";
import { isValidDate } from "oc/utils/is-valid-date";

export const useMyLeaveWorkDays = ({
  contract,
  startDate,
  endDate,
}: {
  contract: string;
  startDate: Date;
  endDate: Date;
}) => {
  let skip = !contract || !isValidDate(startDate) || !isValidDate(endDate);
  const leaveStartYear = moment(startDate).format("YYYY");
  const leaveEndYear = moment(endDate).format("YYYY");
  let nextYear: string | null = null;
  let parsedStartDate = startDate;
  let parsedEndDate = endDate;
  let startOfNextYear;
  let endCurrentOfYear;
  if (leaveEndYear > leaveStartYear) {
    nextYear = leaveEndYear;
    endCurrentOfYear = moment(startDate).endOf("year").toDate();
    startOfNextYear = nextYear
      ? moment(endDate).startOf("year").toDate()
      : null;
    parsedEndDate = endCurrentOfYear;
    parsedStartDate = startOfNextYear || startDate;
  }

  const { data, loading, error, refetch } = useQuery(MY_LEAVE_GET_WORK_DAYS, {
    fetchPolicy: "cache-and-network",
    skip: skip,
    pollInterval: useOcPolling(1000 * 60),
    variables: { contract, startDate, endDate: parsedEndDate },
  });

  const {
    data: nextYearData,
    loading: nextYearLoading,
    error: nextYearError,
    refetch: nextYearRefetch,
  } = useQuery(MY_LEAVE_GET_WORK_DAYS, {
    fetchPolicy: "cache-and-network",
    skip: skip || !nextYear,
    pollInterval: useOcPolling(1000 * 60),
    variables: { contract, startDate: parsedStartDate, endDate },
  });

  useSubscription(MY_LEAVE_WORK_DAYS_SUBSCRIPTION, {
    skip: skip,
    variables: { contract, startDate, endDate },
    onData: () => {
      if (nextYear) {
        nextYearRefetch();
      }
      refetch();
    },
  });

  const workDays = data?.workDays?.workDays || 0;
  const effectiveWorkDays = data?.workDays?.effectiveWorkDays || 0;
  const effectiveWorkDaysByDays = data?.workDays?.effectiveWorkDaysByDays || [];
  const nextYearWorkDays = nextYearData?.workDays?.workDays || 0;
  const nextYearEffectiveWorkDays =
    nextYearData?.workDays?.effectiveWorkDays || 0;
  const nextYearEffectiveWorkDaysByDays =
    nextYearData?.workDays?.effectiveWorkDaysByDays || [];

  return {
    workDays: nextYear ? workDays + nextYearWorkDays : workDays,
    effectiveWorkDays: nextYear
      ? effectiveWorkDays + nextYearEffectiveWorkDays
      : effectiveWorkDays,
    effectiveWorkDaysByDays: nextYear
      ? effectiveWorkDaysByDays + nextYearEffectiveWorkDaysByDays
      : effectiveWorkDaysByDays,
    currentYearWorkDays: workDays,
    currentYearEffectiveWorkDays: effectiveWorkDays,
    nextYearWorkDays: nextYearWorkDays,
    nextYearEffectiveWorkDays: nextYearEffectiveWorkDays,
    workDaysloading: loading || nextYearLoading,
    workDaysError: error || nextYearError,
  };
};

export default useMyLeaveWorkDays;
